import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react'
import { LayoutSplashScreen } from '../../../../_metronic/layout/core'
import { AuthModel, UserModel } from './_models'
import * as authHelper from './AuthHelpers'
import { getOrganisationMedia, getPeopleByToken, getUserByToken, peoplesignout, signout } from './_requests'
import { WithChildren } from '../../../../_metronic/helpers'
import { useMutation } from 'react-query'
import { toast } from 'react-toastify'
type AuthContextProps = {
  auth: AuthModel | undefined 
  permission: any
  savePermission: (permission) => void
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  logout: (access_token?:string) => void
  peopleLogout: (access_token?: string) => void
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  permission: authHelper.getPermission(),
  savePermission: () => { },
  saveAuth: () => { },
  currentUser: undefined,
  setCurrentUser: () => { },
  logout: () => { },
  peopleLogout: () => { }
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({ children }) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [permission, setPermission] = useState<AuthModel | undefined>(authHelper.getPermission())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()
  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }
  const savePermission = (permission) => {
    setPermission(permission)
    if (permission) {
      authHelper.setPermission(permission)
    } else {
      authHelper.removePermission()
    }
  }

  const logoutMutation = (access_token: string) => {
    return signout(access_token);
  };
  const peopleLogoutMutation = (access_token: string) => {
    return peoplesignout(access_token);
  };
  const { mutate } = useMutation(logoutMutation as any);
  const { mutate: mutatePeople } = useMutation(peopleLogoutMutation as any);
  const logout = (access_token?: string) => {
    if (access_token) {
      mutate(access_token as any, {
        onSuccess: () => {
          // Handle the success response
          toast.success('You have been logged out Successfully', {
            theme: 'colored',
          });
          saveAuth(undefined);
          setCurrentUser(undefined);
          savePermission(undefined);
          // Synchronize logout across tabs
          syncLogout();
        },
        onError: () => {
          // Handle logout error
          saveAuth(undefined);
          setCurrentUser(undefined);
          savePermission(undefined);
        },
      });
    } else {
      // Handle logout when no API token is provided
      saveAuth(undefined);
      setCurrentUser(undefined);
      savePermission(undefined);
      // Synchronize logout across tabs
      // syncLogout();
    }
  };
  const peopleLogout = (access_token?: string) => {
    if (access_token) {
      mutatePeople(access_token as any, {
        onSuccess: () => {
          // Handle the success response
          toast.success('You have been logged out Successfully', {
            theme: 'colored',
          });
          saveAuth(undefined);
          setCurrentUser(undefined);
          savePermission(undefined);
          // Synchronize logout across tabs
          syncPeopleLogout();
        },
        onError: () => {
          // Handle logout error
          saveAuth(undefined);
          setCurrentUser(undefined);
          savePermission(undefined);
        },
      });
    } else {
      // Handle logout when no API token is provided
      saveAuth(undefined);
      setCurrentUser(undefined);
      savePermission(undefined);
      // Synchronize logout across tabs
      syncPeopleLogout();
    }
  };
  // Function to synchronize logout across tabs
  const syncLogout = () => {
    // Send a storage event to notify other tabs to logout
    window.localStorage.setItem('logout', Date.now().toString());
  };
  const syncPeopleLogout = () => {
    // Send a storage event to notify other tabs to logout
    window.localStorage.setItem('peoplelogout', Date.now().toString());
  };
  useEffect(() => {
    // Event listener to handle logout event from other tabs
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'logout') {
        logout();
      } else if (event.key === 'peoplelogout') {
        peopleLogout();
      }
    };
    // Attach the event listener
    window.addEventListener('storage', handleStorageChange);
    // Clean up the event listener when the component is unmounted
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return (
    <AuthContext.Provider value={{ auth, permission, savePermission, saveAuth, currentUser, setCurrentUser, logout, peopleLogout }}>
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({ children }) => {
  const { auth, logout, setCurrentUser, peopleLogout } = useAuth()
  const didRequest = useRef(false)
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  // We should request user by authToken (IN OUR EXAMPLE IT'S API_TOKEN) before rendering the application

  useEffect(() => {
    const requestUser = async (apiToken: string) => {
      try {
        if (!didRequest.current) {
          if (auth?.user?.is_people) {
            const media: any = await getOrganisationMedia()
            // Media array se her object ka image_key aur image_value localStorage mein set karna
            media?.data?.data?.forEach((item: { image_key: string; image_value: string }) => {
              if (item.image_key && item.image_value) {
                localStorage.setItem(item.image_key, item.image_value);
              }
            });
            const { data } = await getPeopleByToken(apiToken)

            if (data) {
              setCurrentUser(data)
            }
          } else {
            const { data } = await getUserByToken(apiToken)
            const media: any = await getOrganisationMedia()
            // Media array se her object ka image_key aur image_value localStorage mein set karna
            media?.data?.data?.forEach((item: { image_key: string; image_value: string }) => {
              if (item.image_key && item.image_value) {
                localStorage.setItem(item.image_key, item.image_value);
              }
            })
            if (data) {
              setCurrentUser(data)
            }
          }
        }
      } catch (error) {
        console.error(error)
        if (!didRequest.current) {
          logout()
          peopleLogout()
        }
      } finally {
        setShowSplashScreen(false)
      }

      return () => (didRequest.current = true)
    }

    if (auth && auth.access_token) {
      requestUser(auth.access_token)
    } else {
      logout()
      peopleLogout()
      setShowSplashScreen(false)
    }
    // eslint-disable-next-line
  }, [])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export { AuthProvider, AuthInit, useAuth }
